import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import LoadingState from "@/components/base/LoadingState";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { checkVpsHealth } from "@/services/vpsHealthService";
import { VPS_BACKEND_URL } from "@/config/backend";

/* ─── Types ────────────────────────────────────────────────── */
type IntegrationRow = {
  id: string;
  integration_key: string;
  is_enabled: boolean;
  settings: Record<string, string>;
};

type DomainRequest = {
  id: string;
  requested_domain: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  approved_at: string | null;
};

type Tab = "email" | "whatsapp" | "ai" | "calendar" | "storage" | "domains";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "email",     label: "Email",     icon: "ri-mail-send-line" },
  { id: "whatsapp",  label: "WhatsApp",  icon: "ri-whatsapp-line" },
  { id: "ai",        label: "AI",        icon: "ri-robot-line" },
  { id: "calendar",  label: "Calendar",  icon: "ri-calendar-event-line" },
  { id: "storage",   label: "Storage",   icon: "ri-server-line" },
  { id: "domains",   label: "Domains",   icon: "ri-global-line" },
];

const TIMEZONES = [
  { value: "America/New_York",     label: "Eastern Time (US & Canada)" },
  { value: "America/Chicago",      label: "Central Time (US & Canada)" },
  { value: "America/Denver",       label: "Mountain Time (US & Canada)" },
  { value: "America/Los_Angeles",  label: "Pacific Time (US & Canada)" },
  { value: "America/Mexico_City",  label: "Mexico City" },
  { value: "America/Bogota",       label: "Bogotá, Colombia" },
  { value: "America/Lima",         label: "Lima, Peru" },
  { value: "America/Santiago",     label: "Santiago, Chile" },
  { value: "America/Sao_Paulo",    label: "São Paulo, Brazil" },
  { value: "America/Buenos_Aires", label: "Buenos Aires, Argentina" },
  { value: "Europe/London",        label: "London (GMT/BST)" },
  { value: "Europe/Madrid",        label: "Madrid, Spain" },
  { value: "Europe/Paris",         label: "Paris, France" },
  { value: "Europe/Berlin",        label: "Berlin, Germany" },
  { value: "Asia/Dubai",           label: "Dubai (UAE)" },
  { value: "Asia/Tokyo",           label: "Tokyo, Japan" },
  { value: "Australia/Sydney",     label: "Sydney, Australia" },
];

/* ─── Shared UI ─────────────────────────────────────────────── */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-slate-600 text-xs font-medium mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-slate-400 text-[11px] mt-1">{hint}</p>}
    </div>
  );
}

function TextInput({
  name, value, onChange, placeholder, type = "text", disabled,
}: {
  name: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string; type?: string; disabled?: boolean;
}) {
  return (
    <input
      name={name} type={type} value={value} onChange={onChange}
      placeholder={placeholder} disabled={disabled}
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/70 focus:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
}

function Toggle({
  enabled, onChange, disabled, color = "#C8A96E",
}: {
  enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean; color?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0 ${
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
      }`}
      style={{ backgroundColor: enabled ? color : "#e2e8f0" }}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function SaveBar({
  saving, saved, error, onSave,
}: {
  saving: boolean; saved: boolean; error: string | null; onSave: () => void;
}) {
  return (
    <div className="flex items-center justify-between pt-5 border-t border-slate-100 mt-6">
      <div className="text-xs">
        {error && <span className="flex items-center gap-1.5 text-red-500"><i className="ri-error-warning-line" /> {error}</span>}
        {saved && !error && <span className="flex items-center gap-1.5 text-emerald-600"><i className="ri-check-double-line" /> Saved successfully</span>}
        {!saved && !error && <span className="text-slate-400">Changes persist in Supabase per integration key</span>}
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        {saving
          ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
          : <><i className="ri-save-3-line" />Save Changes</>}
      </button>
    </div>
  );
}

function InfoBanner({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) {
  return (
    <div
      className="flex items-start gap-3 p-3.5 rounded-xl border"
      style={{ backgroundColor: `${color}10`, borderColor: `${color}25` }}
    >
      <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        <i className={`${icon} text-sm`} style={{ color }} />
      </span>
      <div className="text-xs leading-relaxed" style={{ color: `${color}` }}>
        {children}
      </div>
    </div>
  );
}

function StatusDot({ enabled }: { enabled: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${enabled ? "bg-emerald-400" : "bg-slate-300"}`} />
      <span className={`text-xs ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
        {enabled ? "Enabled" : "Disabled"}
      </span>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
export default function AdminIntegrations() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("email");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* ── Integration rows by key ── */
  const [rows, setRows] = useState<Record<string, IntegrationRow>>({});
  const [domainRequests, setDomainRequests] = useState<DomainRequest[]>([]);
  const [vpsTesting, setVpsTesting] = useState(false);
  const [vpsOnline, setVpsOnline] = useState<boolean | null>(null);

  /* ── Local form states per tab ── */
  const [emailForm, setEmailForm] = useState({
    smtp_provider: "smtp2go",
    smtp_from_name: "",
    smtp_from_email: "",
    smtp_enabled: false,
  });

  const [waForm, setWaForm] = useState({
    whatsapp_enabled: false,
    meta_business_id: "",
    phone_number: "",
    template_language: "en_US",
    default_country_code: "+1",
  });

  const [aiForm, setAiForm] = useState({
    ai_assistant_enabled: false,
    ai_provider: "openai",
  });

  const [calForm, setCalForm] = useState({
    calendar_enabled: false,
    calendar_email: "",
    timezone: "America/New_York",
    default_duration_minutes: "120",
    event_title_format: "job_type_client",
  });

  /* ─── Fetch ── */
  const fetchData = useCallback(async (tid: string) => {
    const [intRes, domRes] = await Promise.all([
      supabase.from("tenant_integrations").select("*").eq("tenant_id", tid),
      supabase
        .from("tenant_domain_requests")
        .select("id, requested_domain, status, admin_notes, created_at, approved_at")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false }),
    ]);

    const map: Record<string, IntegrationRow> = {};
    for (const row of (intRes.data ?? []) as IntegrationRow[]) {
      map[row.integration_key] = row;
    }
    setRows(map);

    /* Hydrate form states */
    const smtp = map["smtp2go"];
    if (smtp) {
      setEmailForm({
        smtp_provider: smtp.settings?.smtp_provider ?? "smtp2go",
        smtp_from_name: smtp.settings?.smtp_from_name ?? "",
        smtp_from_email: smtp.settings?.smtp_from_email ?? "",
        smtp_enabled: smtp.is_enabled ?? false,
      });
    }

    const wa = map["whatsapp_meta"];
    if (wa) {
      setWaForm({
        whatsapp_enabled: wa.is_enabled ?? false,
        meta_business_id: wa.settings?.meta_business_id ?? "",
        phone_number: wa.settings?.phone_number ?? "",
        template_language: wa.settings?.template_language ?? "en_US",
        default_country_code: wa.settings?.default_country_code ?? "+1",
      });
    }

    const ai = map["ai_assistant"];
    if (ai) {
      setAiForm({
        ai_assistant_enabled: ai.is_enabled ?? false,
        ai_provider: ai.settings?.ai_provider ?? "openai",
      });
    }

    const cal = map["google_calendar"];
    if (cal) {
      setCalForm({
        calendar_enabled: cal.is_enabled ?? false,
        calendar_email: cal.settings?.calendar_email ?? "",
        timezone: cal.settings?.timezone ?? "America/New_York",
        default_duration_minutes: cal.settings?.default_duration_minutes ?? "120",
        event_title_format: cal.settings?.event_title_format ?? "job_type_client",
      });
    }

    setDomainRequests((domRes.data as DomainRequest[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchData(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  /* ─── VPS Health Test ── */
  const handleTestConnection = async () => {
    setVpsTesting(true);
    try {
      const result = await checkVpsHealth();
      setVpsOnline(result.online);
      if (result.online) {
        Swal.fire({
          icon: "success",
          title: "Backend Online",
          text: `VPS backend responded OK (HTTP ${result.status}).`,
          confirmButtonColor: "#C8A96E",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Backend Offline",
          text: result.error || `HTTP ${result.status}`,
          confirmButtonColor: "#C8A96E",
        });
      }
    } catch (e: unknown) {
      setVpsOnline(false);
      Swal.fire({
        icon: "error",
        title: "Connection Failed",
        text: e instanceof Error ? e.message : "Unknown error",
        confirmButtonColor: "#C8A96E",
      });
    } finally {
      setVpsTesting(false);
    }
  };

  /* ─── Upsert helper ── */
  const upsertIntegration = async (
    key: string,
    is_enabled: boolean,
    settings: Record<string, string>
  ): Promise<string | null> => {
    if (!tenantId) return "No tenant ID";
    const existing = rows[key];
    if (existing) {
      const { error } = await supabase
        .from("tenant_integrations")
        .update({ is_enabled, settings, updated_at: new Date().toISOString() })
        .eq("id", existing.id);
      return error ? error.message : null;
    } else {
      const { data, error } = await supabase
        .from("tenant_integrations")
        .insert({
          tenant_id: tenantId,
          integration_key: key,
          is_enabled,
          settings,
        })
        .select("id")
        .maybeSingle();
      if (!error && data) {
        // Update local rows so next save uses UPDATE instead of INSERT
        setRows((prev) => ({
          ...prev,
          [key]: { id: data.id, integration_key: key, is_enabled, settings },
        }));
      }
      return error ? error.message : null;
    }
  };

  /* ─── Save dispatches by tab ── */
  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    let errMsg: string | null = null;

    try {
      if (activeTab === "email") {
        errMsg = await upsertIntegration("smtp2go", emailForm.smtp_enabled, {
          smtp_provider: emailForm.smtp_provider,
          smtp_from_name: emailForm.smtp_from_name,
          smtp_from_email: emailForm.smtp_from_email,
        });
      } else if (activeTab === "whatsapp") {
        errMsg = await upsertIntegration("whatsapp_meta", waForm.whatsapp_enabled, {
          meta_business_id: waForm.meta_business_id,
          phone_number: waForm.phone_number,
          template_language: waForm.template_language,
          default_country_code: waForm.default_country_code,
        });
      } else if (activeTab === "ai") {
        errMsg = await upsertIntegration("ai_assistant", aiForm.ai_assistant_enabled, {
          ai_provider: aiForm.ai_provider,
        });
      } else if (activeTab === "calendar") {
        errMsg = await upsertIntegration("google_calendar", calForm.calendar_enabled, {
          calendar_email: calForm.calendar_email,
          timezone: calForm.timezone,
          default_duration_minutes: calForm.default_duration_minutes,
          event_title_format: calForm.event_title_format,
        });
      }

      if (errMsg) {
        setSaveError(errMsg);
      } else {
        await fetchData(tenantId);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Tab: Email ── */
  function EmailTab() {
    return (
      <div className="space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Enable Email Integration</p>
            <p className="text-slate-400 text-xs mt-0.5">Send transactional emails via SMTP — contracts, receipts, notifications</p>
          </div>
          <Toggle
            enabled={emailForm.smtp_enabled}
            onChange={(v) => setEmailForm((p) => ({ ...p, smtp_enabled: v }))}
          />
        </div>

        {/* Provider selector */}
        <Field label="SMTP Provider">
          <select
            value={emailForm.smtp_provider}
            onChange={(e) => setEmailForm((p) => ({ ...p, smtp_provider: e.target.value }))}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
          >
            <option value="smtp2go">SMTP2GO</option>
            <option value="sendgrid">SendGrid</option>
            <option value="mailgun">Mailgun</option>
            <option value="ses">Amazon SES</option>
            <option value="custom">Custom SMTP</option>
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="From Name" hint="Display name shown in client's inbox">
            <TextInput
              name="smtp_from_name"
              value={emailForm.smtp_from_name}
              onChange={(e) => setEmailForm((p) => ({ ...p, smtp_from_name: e.target.value }))}
              placeholder="Luna Photography"
            />
          </Field>
          <Field label="From Email" hint="Must be verified in your SMTP provider">
            <TextInput
              name="smtp_from_email"
              type="email"
              value={emailForm.smtp_from_email}
              onChange={(e) => setEmailForm((p) => ({ ...p, smtp_from_email: e.target.value }))}
              placeholder="hello@yourstudio.com"
            />
          </Field>
        </div>

        <InfoBanner icon="ri-shield-keyhole-line" color="#C8A96E">
          <span className="font-semibold">Security note:</span> Your SMTP API key must be stored in{" "}
          <span className="font-mono font-semibold">Supabase Edge Function secrets</span>, not here. This
          form stores only the public configuration (sender name/email, provider).
        </InfoBanner>

        {/* Feature list */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3">Emails handled by this integration:</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              "Contract sent to client",
              "Payment receipt",
              "Contract signing alert",
              "Delivery ready notification",
              "Appointment reminders",
              "Password reset",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <i className={`ri-mail-check-line text-xs ${emailForm.smtp_enabled ? "text-[#C8A96E]" : "text-slate-300"}`} />
                <span className={`text-xs ${emailForm.smtp_enabled ? "text-slate-600" : "text-slate-400"}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ─── Tab: WhatsApp ── */
  function WhatsAppTab() {
    return (
      <div className="space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Enable WhatsApp Messaging</p>
            <p className="text-slate-400 text-xs mt-0.5">Send automated messages via WhatsApp Business Cloud API</p>
          </div>
          <Toggle
            enabled={waForm.whatsapp_enabled}
            onChange={(v) => setWaForm((p) => ({ ...p, whatsapp_enabled: v }))}
            color="#4DB858"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Meta Business ID" hint="Found in Meta Business Manager">
            <TextInput
              name="meta_business_id"
              value={waForm.meta_business_id}
              onChange={(e) => setWaForm((p) => ({ ...p, meta_business_id: e.target.value }))}
              placeholder="123456789012345"
            />
          </Field>
          <Field label="Business Phone Number" hint="WhatsApp registered phone with country code">
            <TextInput
              name="phone_number"
              value={waForm.phone_number}
              onChange={(e) => setWaForm((p) => ({ ...p, phone_number: e.target.value }))}
              placeholder="+1 555 000 0000"
            />
          </Field>
          <Field label="Template Language" hint="Language for approved WhatsApp templates">
            <select
              value={waForm.template_language}
              onChange={(e) => setWaForm((p) => ({ ...p, template_language: e.target.value }))}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
            >
              <option value="en_US">English (US)</option>
              <option value="en_GB">English (UK)</option>
              <option value="es">Spanish</option>
              <option value="es_MX">Spanish (Mexico)</option>
              <option value="pt_BR">Portuguese (Brazil)</option>
              <option value="pt_PT">Portuguese (Portugal)</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </Field>
          <Field label="Default Country Code" hint="Prepended to client phone numbers">
            <select
              value={waForm.default_country_code}
              onChange={(e) => setWaForm((p) => ({ ...p, default_country_code: e.target.value }))}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
            >
              {["+1", "+52", "+55", "+54", "+57", "+34", "+44", "+33", "+49", "+61"].map((cc) => (
                <option key={cc} value={cc}>{cc}</option>
              ))}
            </select>
          </Field>
        </div>

        <InfoBanner icon="ri-shield-keyhole-line" color="#4DB858">
          <span className="font-semibold">Token security:</span> Your WhatsApp Access Token and Phone Number
          ID must be stored as <span className="font-mono font-semibold">Supabase Edge Function secrets</span>. Messages
          are queued in the <span className="font-mono">whatsapp_queue</span> table until the backend sender is wired.
        </InfoBanner>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3">Automated messages this sends:</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              "Shoot day reminder",
              "Contract signed alert",
              "Payment due notice",
              "Gallery delivery ready",
              "Appointment confirmation",
              "Follow-up after shoot",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <i className={`ri-whatsapp-line text-xs ${waForm.whatsapp_enabled ? "text-[#4DB858]" : "text-slate-300"}`} />
                <span className={`text-xs ${waForm.whatsapp_enabled ? "text-slate-600" : "text-slate-400"}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ─── Tab: AI ── */
  function AITab() {
    return (
      <div className="space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Enable AI Assistant</p>
            <p className="text-slate-400 text-xs mt-0.5">Smart contract drafting, message suggestions, lead capture</p>
          </div>
          <Toggle
            enabled={aiForm.ai_assistant_enabled}
            onChange={(v) => setAiForm((p) => ({ ...p, ai_assistant_enabled: v }))}
            color="#C8A96E"
          />
        </div>

        <Field label="AI Provider (placeholder)" hint="Backend provider — configured via Edge Function secrets">
          <select
            value={aiForm.ai_provider}
            onChange={(e) => setAiForm((p) => ({ ...p, ai_provider: e.target.value }))}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
          >
            <option value="openai">OpenAI (GPT-4o)</option>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="gemini">Google Gemini</option>
          </select>
        </Field>

        <InfoBanner icon="ri-robot-line" color="#C8A96E">
          <span className="font-semibold">Full AI configuration</span> is available in{" "}
          <button
            type="button"
            onClick={() => navigate("/admin/settings/ai-assistant")}
            className="underline font-semibold cursor-pointer"
          >
            Settings → AI Assistant
          </button>
          . This tab controls only the on/off toggle and provider selection stored in{" "}
          <span className="font-mono">tenant_integrations</span>.
        </InfoBanner>

        {/* Feature grid */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3">AI-powered features:</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              "Contract text drafting",
              "Client message suggestions",
              "Photo caption generation",
              "Lead capture chat widget",
              "Smart reminder timing",
              "Conversation summaries",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <i className={`ri-sparkling-2-line text-xs ${aiForm.ai_assistant_enabled ? "text-[#C8A96E]" : "text-slate-300"}`} />
                <span className={`text-xs ${aiForm.ai_assistant_enabled ? "text-slate-600" : "text-slate-400"}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex">
          <button
            type="button"
            onClick={() => navigate("/admin/settings/ai-assistant")}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-[#C8A96E]/60 hover:text-[#a6853e] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-robot-line" />
            Configure AI Assistant in full detail →
          </button>
        </div>
      </div>
    );
  }

  /* ─── Tab: Calendar ── */
  function CalendarTab() {
    const EVENT_FORMATS = [
      { value: "job_type_client", label: "Job Type + Client", example: "Wedding · Sarah & Tom" },
      { value: "client_job_type", label: "Client + Job Type", example: "Sarah & Tom · Wedding" },
      { value: "job_title_only",  label: "Job Title Only",   example: "Johnson Wedding — Full Day" },
      { value: "client_only",     label: "Client Only",      example: "Sarah Johnson" },
    ];

    return (
      <div className="space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Enable Calendar Sync</p>
            <p className="text-slate-400 text-xs mt-0.5">Sync confirmed jobs to Google Calendar automatically</p>
          </div>
          <Toggle
            enabled={calForm.calendar_enabled}
            onChange={(v) => setCalForm((p) => ({ ...p, calendar_enabled: v }))}
            color="#34A853"
          />
        </div>

        <Field label="Calendar Email / ID" hint="Your Gmail address or secondary calendar ID">
          <TextInput
            name="calendar_email"
            type="email"
            value={calForm.calendar_email}
            onChange={(e) => setCalForm((p) => ({ ...p, calendar_email: e.target.value }))}
            placeholder="studio@gmail.com"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Timezone">
            <select
              value={calForm.timezone}
              onChange={(e) => setCalForm((p) => ({ ...p, timezone: e.target.value }))}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Default Event Duration">
            <select
              value={calForm.default_duration_minutes}
              onChange={(e) => setCalForm((p) => ({ ...p, default_duration_minutes: e.target.value }))}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
            >
              {[30, 60, 90, 120, 180, 240, 300, 360, 480].map((m) => (
                <option key={m} value={String(m)}>
                  {m < 60 ? `${m} min` : m === 60 ? "1 hour" : `${m / 60} hours`}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Event Title Format">
          <div className="grid grid-cols-2 gap-2 mt-1">
            {EVENT_FORMATS.map((fmt) => {
              const active = calForm.event_title_format === fmt.value;
              return (
                <button
                  key={fmt.value}
                  type="button"
                  onClick={() => setCalForm((p) => ({ ...p, event_title_format: fmt.value }))}
                  className={`flex flex-col gap-1 p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                    active
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <p className={`text-xs font-semibold ${active ? "text-emerald-700" : "text-slate-500"}`}>
                    {fmt.label}
                  </p>
                  <p className="text-slate-400 text-[10px] font-mono">{fmt.example}</p>
                </button>
              );
            })}
          </div>
        </Field>

        <InfoBanner icon="ri-shield-keyhole-line" color="#34A853">
          Google OAuth credentials must be stored in{" "}
          <span className="font-mono font-semibold">Supabase Edge Function secrets</span>. No events
          are created until OAuth is wired in the backend. This page stores configuration only.
        </InfoBanner>
      </div>
    );
  }

  /* ─── Tab: Storage ── */
  function StorageTab() {
    const statusColor = vpsOnline === null ? "#94a3b8" : vpsOnline ? "#10b981" : "#ef4444";
    const statusLabel = vpsOnline === null ? "Unknown — run test" : vpsOnline ? "Online" : "Offline";
    const statusDot = vpsOnline === null ? "bg-slate-300" : vpsOnline ? "bg-emerald-400" : "bg-red-400";

    return (
      <div className="space-y-5">
        {/* VPS Backend Status Card */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-slate-200 shrink-0">
            <i className="ri-server-line text-xl text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-slate-700 text-sm font-semibold">VPS Storage Backend</p>
            <p className="text-slate-400 text-xs mt-0.5 truncate font-mono">{VPS_BACKEND_URL}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${statusDot}`} />
              <span className="text-xs font-medium" style={{ color: statusColor }}>
                {statusLabel}
              </span>
            </div>
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={vpsTesting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-[#C8A96E]/60 hover:text-[#a6853e] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {vpsTesting ? (
                <div className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
              ) : (
                <i className="ri-refresh-line" />
              )}
              Test Connection
            </button>
          </div>
        </div>

        {/* Architecture grid */}
        <div className="grid grid-cols-2 gap-4">
          {[
            {
              icon: "ri-upload-cloud-line",
              title: "Uploads",
              desc: "Files are uploaded directly to the VPS via the platform backend. The frontend never handles raw file storage.",
            },
            {
              icon: "ri-database-2-line",
              title: "Metadata",
              desc: "All file references (URL, size, type, ownership) are stored in the Supabase uploaded_files table for fast querying.",
            },
            {
              icon: "ri-key-2-line",
              title: "Access Control",
              desc: "Files are served via signed URLs generated by the backend. Supabase RLS ensures only authorized users access tenant files.",
            },
            {
              icon: "ri-shield-check-line",
              title: "Security",
              desc: "No direct Supabase Storage used. All file I/O goes through Edge Functions or VPS backend endpoints.",
            },
          ].map((item) => (
            <div key={item.title} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-lg">
                  <i className={`${item.icon} text-sm text-slate-600`} />
                </span>
                <p className="text-slate-700 text-xs font-semibold">{item.title}</p>
              </div>
              <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-information-line text-slate-400" />
            Storage Architecture Notes
          </p>
          <ul className="space-y-1.5">
            {[
              "Files upload via UploadField component → VPS backend → URL stored in uploaded_files",
              "Each file row includes: tenant_id, uploader_id, url, size_bytes, mime_type, purpose",
              "Quota tracked in saas_tenants.storage_used_mb — updated on each upload",
              "Max quota enforced per plan in saas_tenants.storage_quota_mb",
              "File deletion removes both the VPS file and the metadata row",
            ].map((note) => (
              <li key={note} className="flex items-start gap-2 text-slate-500 text-xs">
                <i className="ri-arrow-right-s-line text-slate-300 mt-0.5 shrink-0" />
                {note}
              </li>
            ))}
          </ul>
        </div>

        <InfoBanner icon="ri-information-line" color="#94a3b8">
          This tab is <span className="font-semibold">read-only</span>. Storage configuration is managed
          by the Super Admin in the SA panel. Tenants cannot change their storage provider.
        </InfoBanner>
      </div>
    );
  }

  /* ─── Tab: Domains ── */
  function DomainsTab() {
    function DomainStatusBadge({ status }: { status: string }) {
      const map: Record<string, { label: string; cls: string; icon: string }> = {
        approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700", icon: "ri-check-double-line" },
        pending:  { label: "Pending",  cls: "bg-amber-100 text-amber-700",   icon: "ri-time-line" },
        rejected: { label: "Rejected", cls: "bg-red-100 text-red-600",       icon: "ri-close-line" },
      };
      const d = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-600", icon: "ri-question-line" };
      return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full ${d.cls}`}>
          <i className={`${d.icon} text-xs`} />
          {d.label}
        </span>
      );
    }

    return (
      <div className="space-y-5">
        {domainRequests.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
              <i className="ri-global-line text-2xl text-slate-400" />
            </div>
            <p className="text-slate-600 text-sm font-semibold mb-1">No domain requests yet</p>
            <p className="text-slate-400 text-xs mb-4 max-w-xs mx-auto">
              Request a custom domain to use your own branded URL for the client portal and public website.
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin/settings/domain")}
              className="px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Request Custom Domain →
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {domainRequests.map((req) => (
              <div key={req.id} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-slate-800 font-semibold text-sm font-mono">{req.requested_domain}</p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      Requested {new Date(req.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <DomainStatusBadge status={req.status} />
                </div>

                {req.status === "approved" && (
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg p-3 text-xs">
                    <i className="ri-check-double-line shrink-0" />
                    <span>
                      Domain approved{req.approved_at ? ` on ${new Date(req.approved_at).toLocaleDateString()}` : ""}.
                      Point your DNS to the platform's IP to activate.
                    </span>
                  </div>
                )}

                {req.status === "pending" && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3 text-xs">
                    <i className="ri-time-line shrink-0" />
                    <span>Under review by platform admin. Usually takes 24–48h.</span>
                  </div>
                )}

                {req.status === "rejected" && req.admin_notes && (
                  <div className="flex items-start gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-xs">
                    <i className="ri-close-circle-line shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold block mb-0.5">Rejection note:</span>
                      {req.admin_notes}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => navigate("/admin/settings/domain")}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-[#C8A96E]/60 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Request another domain
            </button>
          </div>
        )}

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-information-line text-slate-400" />
            How custom domains work
          </p>
          <ul className="space-y-1.5">
            {[
              "Submit a domain request from Settings → Domain",
              "Platform admin reviews and approves the request",
              "Point your domain DNS to the platform's IP (provided after approval)",
              "SSL certificate is provisioned automatically",
              "Your portal and public website become accessible via your domain",
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-2.5 text-slate-500 text-xs">
                <span className="w-4 h-4 flex items-center justify-center bg-slate-200 rounded-full text-[10px] font-bold text-slate-500 shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const showSaveBar = activeTab !== "storage" && activeTab !== "domains";

  const tabContent: Record<Tab, React.ReactNode> = {
    email:    <EmailTab />,
    whatsapp: <WhatsAppTab />,
    ai:       <AITab />,
    calendar: <CalendarTab />,
    storage:  <StorageTab />,
    domains:  <DomainsTab />,
  };

  /* ─── Derived status per tab ── */
  const tabStatus: Record<Tab, boolean> = {
    email:    rows["smtp2go"]?.is_enabled ?? false,
    whatsapp: rows["whatsapp_meta"]?.is_enabled ?? false,
    ai:       rows["ai_assistant"]?.is_enabled ?? false,
    calendar: rows["google_calendar"]?.is_enabled ?? false,
    storage:  vpsOnline ?? true,
    domains:  domainRequests.some((d) => d.status === "approved"),
  };

  /* ─── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Integrations"
        subtitle="Configure external services — email, messaging, AI, calendar and domains"
      />
      <div className="px-4 md:px-8 py-6">
        {loading ? (
          <LoadingState />
        ) : (
          <div className="max-w-4xl">
            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1.5 mb-6 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSaved(false);
                    setSaveError(null);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap relative ${
                    activeTab === tab.id
                      ? "bg-[#0f172a] text-white"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                  {/* Status dot */}
                  {tab.id !== "storage" && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        tabStatus[tab.id] ? "bg-emerald-400" : "bg-slate-300"
                      } ${activeTab === tab.id ? "opacity-100" : ""}`}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {/* Tab header */}
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
                    <i className={`${TABS.find((t) => t.id === activeTab)?.icon} text-slate-600`} />
                  </div>
                  <div>
                    <p className="text-[#0f172a] font-semibold text-sm">
                      {TABS.find((t) => t.id === activeTab)?.label}
                    </p>
                    {activeTab !== "storage" && activeTab !== "domains" && (
                      <StatusDot enabled={tabStatus[activeTab]} />
                    )}
                  </div>
                </div>
                {activeTab !== "storage" && activeTab !== "domains" && (
                  <span className="text-slate-400 text-[11px] font-mono bg-slate-50 px-2.5 py-1 rounded-md">
                    tenant_integrations
                  </span>
                )}
              </div>

              {tabContent[activeTab]}

              {showSaveBar && (
                <SaveBar
                  saving={saving}
                  saved={saved}
                  error={saveError}
                  onSave={handleSave}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}